import React, { useEffect, useState, useCallback, useRef } from "react"
import { getTargetDate } from '../../control/Constant';

// Custom Input Component that maintains focus
const FocusPreservingInput = ({ initialValue, onValueChange, style, ...props }) => {
    const [localValue, setLocalValue] = useState(initialValue || '')
    const inputRef = useRef(null)
    const timeoutRef = useRef(null)
    
    // Update local value when initial value changes from parent
    useEffect(() => {
        if (initialValue !== localValue && document.activeElement !== inputRef.current) {
            setLocalValue(initialValue || '')
        }
    }, [initialValue])
    
    const handleChange = (e) => {
        const newValue = e.target.value
        setLocalValue(newValue)
        
        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        
        // Debounce the parent update
        timeoutRef.current = setTimeout(() => {
            onValueChange(newValue)
        }, 300)
    }
    
    const handleBlur = () => {
        // Immediate update on blur
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        onValueChange(localValue)
    }
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])
    
    return (
        <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            style={style}
            {...props}
        />
    )
}

const ARDeptReport = (props) => {
    const [editableData, setEditableData] = useState({})
    const inputRefs = useRef({})

    // Define which rows should be editable
    const editableRows = [
        'mtdInitialsHeld',
        'mtdRetained', 
        'mtdRevenueRetained',
        'mtdRevenueCollectedFromInitials',
        'mtdRevenueCollectedNotFromInitials'
    ]

    // Get current user ID for user-specific storage
    const getCurrentUserId = () => {
        return sessionStorage.getItem('loggedUserId') || sessionStorage.getItem('SessPrimaryUserId') || ''
    }

    // Load saved data from localStorage on component mount (user-specific)
    useEffect(() => {
        const userId = getCurrentUserId()
        const savedData = localStorage.getItem(`arDeptReportEditableData_${userId}`)
        if (savedData) {
            setEditableData(JSON.parse(savedData))
        }
    }, [])

    // Handle input changes with immediate state update
    const handleCellChange = useCallback((rowKey, columnKey, value) => {
        setEditableData(prevData => {
            const newData = {
                ...prevData,
                [rowKey]: {
                    ...prevData[rowKey],
                    [columnKey]: value
                }
            }
            
            // Save to localStorage (user-specific)
            const userId = getCurrentUserId()
            localStorage.setItem(`arDeptReportEditableData_${userId}`, JSON.stringify(newData))
            return newData
        })
    }, [])

    // Helper function to parse numeric values from strings
    const parseNumericValue = (value) => {
        if (!value) return 0
        const str = String(value).trim()
        // Bracket notation like $(345) means negative
        const isNegative = str.includes('(') && str.includes(')')
        const cleanValue = str.replace(/[$,()%]/g, '').trim()
        const num = parseFloat(cleanValue)
        if (isNaN(num)) return 0
        return isNegative ? -num : num
    }

    // Helper function to format numbers back to display format
    const formatDisplayValue = (value, isPercentage = false) => {
        if (!value && value !== 0) return ''
        if (isPercentage) {
            return `${value}%`
        }
        if (value < 0) {
            return `$(${Math.abs(value).toLocaleString()})`
        }
        return `$${value.toLocaleString()}`
    }

    // Get the display value for a cell (editable data takes precedence)
    const getCellValue = (rowKey, columnKey, originalValue) => {
        if (editableData[rowKey] && editableData[rowKey][columnKey] !== undefined) {
            return editableData[rowKey][columnKey]
        }
        return originalValue
    }

    // Calculate totals automatically
    const calculateTotal = useCallback((rowKey, columnKey) => {
        if (columnKey !== 'total') return null
        
        const columns = ['legalLifePlanning', 'crisis', 'probate', 'seminar', 'ama', 'other', 'refund']
        let total = 0
        
        columns.forEach(col => {
            // Get value directly from editableData or original data
            let value = 0
            if (editableData[rowKey] && editableData[rowKey][col] !== undefined) {
                value = editableData[rowKey][col]
            } else {
                value = props.dashboardData?.sheetData?.[rowKey]?.[col] || 0
            }
            const parsed = parseNumericValue(value)
            // refund always subtracts from total
            total += col === 'refund' ? -Math.abs(parsed) : parsed
        })
        
        return total
    }, [editableData, props.dashboardData])

    // Calculate all dependent fields automatically for AR Department
    const calculateDependentFields = useCallback((rowKey, columnKey, originalValue) => {
        // 1. Closed Percentage = (MTD Retained / MTD Initials Held) * 100
        if (rowKey === 'closedPercentage') {
            let retainedValue = 0
            if (editableData['mtdRetained'] && editableData['mtdRetained'][columnKey] !== undefined) {
                retainedValue = editableData['mtdRetained'][columnKey]
            } else {
                retainedValue = props.dashboardData?.sheetData?.mtdRetained?.[columnKey] || 0
            }
            
            let heldValue = 0
            if (editableData['mtdInitialsHeld'] && editableData['mtdInitialsHeld'][columnKey] !== undefined) {
                heldValue = editableData['mtdInitialsHeld'][columnKey]
            } else {
                heldValue = props.dashboardData?.sheetData?.mtdInitialsHeld?.[columnKey] || 0
            }
            
            const retained = parseNumericValue(retainedValue)
            const held = parseNumericValue(heldValue)
            
            if (held === 0) return '0%'
            const percentage = ((retained / held) * 100).toFixed(1)
            return `${percentage}%`
        }

        // 2. TOTAL COLLECTED = MTD Revenue Collected From Initials + MTD Revenue Collected not from initials
        if (rowKey === 'totalCollected') {
            let fromInitialsValue = 0
            if (editableData['mtdRevenueCollectedFromInitials'] && editableData['mtdRevenueCollectedFromInitials'][columnKey] !== undefined) {
                fromInitialsValue = editableData['mtdRevenueCollectedFromInitials'][columnKey]
            } else {
                fromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedFromInitials?.[columnKey] || 0
            }
            
            let notFromInitialsValue = 0
            if (editableData['mtdRevenueCollectedNotFromInitials'] && editableData['mtdRevenueCollectedNotFromInitials'][columnKey] !== undefined) {
                notFromInitialsValue = editableData['mtdRevenueCollectedNotFromInitials'][columnKey]
            } else {
                notFromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedNotFromInitials?.[columnKey] || 0
            }
            
            const fromInitials = parseNumericValue(fromInitialsValue)
            const notFromInitials = parseNumericValue(notFromInitialsValue)
            const totalCollected = fromInitials + notFromInitials
            
            return formatDisplayValue(totalCollected)
        }

        return null
    }, [editableData, props.dashboardData])

    // Enhanced getCellValue with all calculations
    const getCellValueWithCalculations = useCallback((rowKey, columnKey, originalValue) => {
        // Check for manual edits first
        if (editableData[rowKey] && editableData[rowKey][columnKey] !== undefined) {
            return editableData[rowKey][columnKey]
        }
        
        // Auto-calculate totals for editable rows
        if (editableRows.includes(rowKey) && columnKey === 'total') {
            const calculatedTotal = calculateTotal(rowKey, columnKey)
            if (calculatedTotal !== null) {
                return formatDisplayValue(calculatedTotal)
            }
        }
        
        // Auto-calculate all dependent fields
        const calculatedValue = calculateDependentFields(rowKey, columnKey, originalValue)
        if (calculatedValue !== null) {
            return calculatedValue
        }
        
        return originalValue
    }, [editableData, calculateTotal, calculateDependentFields])

    // EditableCell component using custom focus-preserving input
    const EditableCell = ({ rowKey, columnKey, data, className, isColumnEditable, displayValue }) => {
        const inputValue = getCellValue(rowKey, columnKey, data?.[columnKey] || '')
        
        const handleValueChange = useCallback((value) => {
            handleCellChange(rowKey, columnKey, value)
        }, [rowKey, columnKey])
        
        return (
            <td className={className}>
                {isColumnEditable ? (
                    <FocusPreservingInput
                        initialValue={inputValue}
                        onValueChange={handleValueChange}
                        style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '13px',
                            padding: '2px 4px',
                            fontFamily: 'inherit'
                        }}
                    />
                ) : (
                    displayValue
                )}
            </td>
        )
    }

    const EditableRow = ({ label, data, isGreenText = false, rowKey }) => {
        const isEditable = editableRows.includes(rowKey)
        
        return (
            <tr>
                <td className="left">{label}</td>
                {['legalLifePlanning', 'crisis', 'probate', 'seminar', 'ama', 'other', 'refund', 'total'].map((columnKey, index) => {
                    const classNames = ['legal-life-planning', 'crisis', 'probate', 'seminar-ar', 'ama-ar', 'other-ar', 'refund', isGreenText ? 'total-collected' : '']
                    const displayValue = getCellValueWithCalculations(rowKey, columnKey, data?.[columnKey] || '')
                    const isColumnEditable = isEditable && columnKey !== 'total' // Don't allow editing total column
                    
                    return (
                        <EditableCell
                            key={`${rowKey}-${columnKey}`}
                            rowKey={rowKey}
                            columnKey={columnKey}
                            data={data}
                            className={classNames[index]}
                            isColumnEditable={isColumnEditable}
                            displayValue={displayValue}
                        />
                    )
                })}
            </tr>
        )
    }

    function ARDepartmentDashboard({ selectedMonth, selectedYear, dashboardData }) {
        const { sheetData, revenueCollected } = dashboardData;
        const monthsList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        const formatDate = (date) => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const dd = String(date.getDate()).padStart(2, "0");
            return `${mm}/${dd}/${yyyy}`;
        };

        const yesterDaysDate = getTargetDate(selectedMonth, selectedYear, 'MM/DD/YYYY');

        return (
            <div className="sheet">
                {/* Top Table */}
                <table className="topTable ar-dept-report">
                    <thead>
                        <tr>
                            <th>{monthsList[Number(selectedMonth) - 1]}</th>
                            <th className="legal-life-planning">LEGAL-LIFEPLANNING</th>
                            <th className="crisis">CRISIS</th>
                            <th className="probate">PROBATE</th>
                            <th className="seminar-ar">Seminar/Initial</th>
                            <th className="ama-ar">AMA</th>
                            <th className="other-ar">Other(Immigration, Litigation, Radio Sponsorship)</th>
                            <th className="refund">Refund</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <EditableRow label="MTD Initials/Demos Held" data={sheetData?.mtdInitialsHeld} rowKey="mtdInitialsHeld" />
                        <EditableRow label="MTD Retained" data={sheetData?.mtdRetained} rowKey="mtdRetained" />
                        <EditableRow label="Closed Percentage" data={sheetData?.closedPercentage} rowKey="closedPercentage" />
                        <EditableRow label="MTD Revenue Retained" data={sheetData?.mtdRevenueRetained} rowKey="mtdRevenueRetained" />
                        <EditableRow label="MTD Revenue Collected From Initials" data={sheetData?.mtdRevenueCollectedFromInitials} rowKey="mtdRevenueCollectedFromInitials" />
                        <EditableRow label="MTD Revenue Collected not from initials" data={sheetData?.mtdRevenueCollectedNotFromInitials} rowKey="mtdRevenueCollectedNotFromInitials" />
                        <EditableRow label="TOTAL COLLECTED" data={sheetData?.totalCollected} isGreenText={true} rowKey="totalCollected" />
                    </tbody>
                </table>

                {/* Revenue Table */}
                <table className="bottomTable ar-dept-report">
                    <thead>
                        <tr>
                            <th colSpan={1}>REVENUE COLLECTED</th>
                            <th colSpan={2}>DAY</th>
                            <th colSpan={2}>WEEK</th>
                            <th colSpan={2}>MONTH</th>
                            <th colSpan={2}>YTD</th>
                        </tr>
                        <tr className="trBgWhite">
                            <th>{yesterDaysDate}</th>
                            <th>Daily Goal</th>
                            <th>Yesterday Actual</th>
                            <th>Weekly Goal</th>
                            <th>Wtd Actual</th>
                            <th>Monthly Goal</th>
                            <th>Mtd Actual</th>
                            <th>Yearly Goal</th>
                            <th>Ytd Actual</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(revenueCollected).map(([key, val], index) => {
                            // Map the key names to proper display names
                            const getDisplayName = (keyName) => {
                                switch(keyName) {
                                    case 'LegalLifePlanning': return 'LEGAL-LIFEPLANNING';
                                    case 'Crisis': return 'CRISIS';
                                    case 'Probate': return 'PROBATE';
                                    case 'Seminar': return 'SEMINAR';
                                    case 'Ama': return 'AMA';
                                    case 'Other': return 'OTHER';
                                    case 'Refunds': return 'Refund';
                                    case 'Total': return 'TOTAL';
                                    default: return keyName.toUpperCase();
                                }
                            };
                            const getRowClass = (keyName) => {
                                switch(keyName) {
                                    case 'LegalLifePlanning': return 'legallifeplanning';
                                    case 'Crisis': return 'crisis';
                                    case 'Probate': return 'probate';
                                    case 'Seminar': return 'seminar';
                                    case 'Ama': return 'ama';
                                    case 'Other': return 'other';
                                    case 'Refunds': return 'refund';
                                    case 'Total': return 'total';
                                    default: return key.toLowerCase().replace(/[^a-z]/g, '');
                                }
                            };
                            
                            const rowClass = getRowClass(key);
                            
                            return (
                                <tr key={key} className={rowClass}>
                                    <td className={rowClass}>{getDisplayName(key)}</td>
                                    <td className={rowClass}>{val?.daily}</td>
                                    <td className={rowClass}>{val?.yesterday}</td>
                                    <td className={rowClass}>{val?.weekly}</td>
                                    <td className={rowClass}>{val?.wtd}</td>
                                    <td className={rowClass}>{val?.monthly}</td>
                                    <td className={rowClass}>{val?.mtd}</td>
                                    <td className={rowClass}>{val?.yearly}</td>
                                    <td className={rowClass}>{val?.ytd}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <>
            <ARDepartmentDashboard 
                selectedMonth={props?.selectedMonth} 
                selectedYear={props?.selectedYear} 
                dashboardData={props?.dashboardData || {
                    sheetData: {
                        mtdInitialsHeld: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 },
                        mtdRetained: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 },
                        closedPercentage: { total: "", legalLifePlanning: "", crisis: "", probate: "", seminar: "", ama: "", other: "", refund: "" },
                        mtdRevenueRetained: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 },
                        mtdRevenueCollectedFromInitials: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 },
                        mtdRevenueCollectedNotFromInitials: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 },
                        totalCollected: { total: 0, legalLifePlanning: 0, crisis: 0, probate: 0, seminar: 0, ama: 0, other: 0, refund: 0 }
                    },
                    revenueCollected: {
                        LegalLifePlanning: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Crisis: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Probate: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Seminar: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Ama: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Other: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Refunds: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" },
                        Total: { daily: "", yesterday: "", weekly: "", wtd: "", monthly: "", mtd: "", yearly: "", ytd: "" }
                    }
                }}
            />
        </>
    )
};

export default ARDeptReport;